import {
  Box,
  Container,
  Grid,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { ReactComponent as ChecklistSvg } from "assets/images/technology-platform/clipboard_checklist.svg";
import { ReactComponent as MicroscopeSvg } from "assets/images/technology-platform/microscope.svg";
import { ReactComponent as ResearchSvg } from "assets/images/technology-platform/research.svg";
import { ReactComponent as TestTubeSvg } from "assets/images/technology-platform/test_tube.svg";
import { AMP, NGS } from "components/acronyms";

import { WorkflowStepProps } from "./type";
import { WorkflowStep } from "./WorkflowStep";

const WorkflowStepItems: WorkflowStepProps[] = [
  {
    title: "Single-use, lyophilized reagents",
    content: [
      "Eliminate unnecessary master mixes",
      "Minimize risk of user-error and contamination",
      "Room temperature stable",
    ],
    StepImage: MicroscopeSvg,
  },
  {
    title: "Consistent, fast workflows",
    content: [
      "1-1.5 day library prep with minimal hands-on time",
      "Color-coded reagent pouches and protocols",
    ],
    StepImage: ResearchSvg,
  },
  {
    title: "Flexible",
    content: ["8-strip tubes", "96-well 'breakaway' plates"],
    StepImage: TestTubeSvg,
  },
  {
    title: "Unified workflows",
    content: [
      <>
        All tests leverage <AMP /> enrichment chemistry so workflows
        significantly overlap. Our products can be run individually or easily
        batched to provide flexibility of throughput.
      </>,
    ],
    StepImage: ChecklistSvg,
  },
];

export const SimpleWorkflow = () => {
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  return (
    <Box
      component="section"
      className="default-bg"
      pt={{ xs: 3, md: 5, lg: 10 }}
      pb={{ xs: 4, md: 7, lg: 12 }}
    >
      <Container>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography component="h2" align="center">
            <Box
              component="span"
              display="block"
              textAlign="center"
              fontSize={xs ? 21 : 32}
              lineHeight={xs ? "30.62px" : "46.66px"}
              fontWeight={600}
              letterSpacing={0.5}
            >
              Surprisingly simple <NGS /> workflow
            </Box>
          </Typography>
        </Box>
        <Box
          width={{ xs: "unset", sm: "unset", md: 759 }}
          margin="auto"
          mt={4}
          mb={{ xs: 2, sm: 3, md: 4 }}
        >
          <Typography variant="subtitle1" component="p" align="center">
            Designed with simplicity in mind, we make it easy to implement{" "}
            <NGS /> research assays in any lab. Combined with lyophilized,
            single-use reagents, tech-friendly workflows reduce labor, minimize
            the potential for user error and eliminate the need for master mixes
            no matter what panel you choose.
          </Typography>
        </Box>
        <Grid container spacing={3} justifyContent="center" component="ul">
          {WorkflowStepItems.map((item: WorkflowStepProps) => (
            <Grid item xs={12} sm={6} md={3} key={item.title} component="li">
              <WorkflowStep {...item} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
